import React, { useState, useRef, useEffect } from "react";
import api from "../../services/api";
import AIDisclosureModal, { hasAcceptedAIDisclosure, recordAIDisclosureAcceptance } from "../copilot/AIDisclosureModal";

/**
 * A small, purpose-built chat widget for the ACCBF demo — deliberately NOT
 * a reskin of CareCopilotWidget.jsx. That component is built specifically
 * around amyloidosis (context tabs like "My Diagnosis," "Lab Results,"
 * subscription/upsell UI, daily check-in flows) — none of which apply here.
 *
 * This reuses the one thing that's actually hard and already proven: the
 * backend AI endpoint (Claude via AWS Bedrock). Everything UI-specific is
 * built fresh and scoped tightly to what this demo needs to show: that the
 * same underlying engine can be pointed at a different disease area with a
 * different system prompt, in an afternoon — not a new integration project.
 */

const SYSTEM_PROMPT = `You are a helpful assistant for families navigating a pediatric cancer diagnosis, built for the American Children's Cancer Benevolence Fund (ACCBF). Help parents and caregivers understand clinical trial options, find local support resources, and answer general questions about pediatric oncology in plain, compassionate, age-appropriate language. Always remind users to discuss any medical decisions with their child's care team, and that you are an informational assistant, not a substitute for medical advice.`;

const SYSTEM_PROMPT_TRIALS = `You are a helpful assistant for families navigating a pediatric cancer diagnosis, built for the American Children's Cancer Benevolence Fund (ACCBF). You will be given real, current clinical trial data pulled directly from ClinicalTrials.gov and the National Cancer Institute. Explain ONLY what is in the provided data, in plain, compassionate, age-appropriate language — do not add, infer, or invent any trial details beyond what's given. If no trials were found in the data, say so clearly rather than suggesting alternatives you're not certain exist. Always remind users to discuss any trial with their child's care team, and that you are an informational assistant, not a substitute for medical advice.`;

/**
 * Free, local (no backend call) best-effort extraction of a clean
 * condition term from a natural-language question — e.g. "What clinical
 * trials are available for leukemia?" -> "leukemia". Used for the
 * "Search real trials" button in General mode, where a real LLM
 * extraction call isn't worth doubling the backend hit count against
 * the shared per-IP free-question limit. Trials mode still uses the
 * real LLM extraction (see below), since retrieval accuracy there
 * genuinely benefits from it.
 */
function extractConditionHeuristic(text) {
  let cleaned = (text || "").trim();
  cleaned = cleaned.replace(/^(what|which)\s+(clinical\s+)?trials?\s+(are\s+)?(available|exist)?\s*(for|about|on|regarding)\s+/i, "");
  cleaned = cleaned.replace(/^(are there|is there)\s+(any\s+)?(clinical\s+)?trials?\s+(for|about|on|regarding)\s+/i, "");
  cleaned = cleaned.replace(/^(find|search|show me|list)\s+(clinical\s+)?trials?\s+(for|about|on|regarding)\s+/i, "");
  cleaned = cleaned.replace(/^(tell me about|explain|what is|what's)\s+/i, "");
  cleaned = cleaned.replace(/[?.!]+$/, "").trim();
  return cleaned || text.trim();
}

const ACCENT = "#B45309";
const INK = "#0F172A";

function renderMarkdown(text) {
  return (text || "")
    .replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>")
    .replace(/\*(.*?)\*/g, "<em>$1</em>")
    .replace(/^### (.*$)/gm, "<h4 style='margin:8px 0 4px;font-size:14px;'>$1</h4>")
    .replace(/^## (.*$)/gm, "<h3 style='margin:10px 0 4px;font-size:15px;'>$1</h3>")
    .replace(/^# (.*$)/gm, "<h2 style='margin:10px 0 6px;font-size:16px;'>$1</h2>")
    .replace(/^- (.*$)/gm, "<div style='padding-left:12px;'>• $1</div>")
    .replace(/\n/g, "<br>");
}

export default function AccbfCopilotWidget({ onSearchRequest, interpretRequest }) {
  const [mode, setMode] = useState("general"); // "general" | "trials"
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [messages, setMessages] = useState([
    { role: "assistant", content: "Hi — I'm here to help you find clinical trials, local resources, or just answer questions about your child's diagnosis. What can I help with?" },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [showDisclosure, setShowDisclosure] = useState(false);
  const [pendingQuestion, setPendingQuestion] = useState(null);
  const endRef = useRef(null);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, loading]);

  const sendMessage = async (question, systemPromptOverride) => {
    if (!question?.trim() || loading) return;

    if (!hasAcceptedAIDisclosure()) {
      setPendingQuestion(question);
      setShowDisclosure(true);
      return;
    }

    setInput("");
    setMessages((prev) => [...prev, { role: "user", content: question }]);
    setLoading(true);
    try {
      // "Interpret with Copilot" already has real, verified data in hand —
      // skip extraction and retrieval entirely and just explain it.
      if (systemPromptOverride) {
        const response = await api.post("/v1/copilot/ask", {
          question,
          systemPrompt: systemPromptOverride,
          contextId: "accbf-interpret",
        });
        const answer = response.data?.answer || "I wasn't able to process that — please try again.";
        setMessages((prev) => [...prev, { role: "assistant", content: answer }]);
        return;
      }

      let payload = { question, systemPrompt: SYSTEM_PROMPT, contextId: "accbf-pediatric" };

      // In General mode, use the free local heuristic — no extra backend
      // call. Only Trials mode makes a real extraction call, since
      // retrieval accuracy there genuinely depends on it. This roughly
      // halves real backend hits for ordinary questions, which matters
      // because these calls count against a shared per-IP free-question
      // limit with regular NephroTrax's own anonymous Care Copilot.
      let extractedTermForButton = extractConditionHeuristic(question);

      if (mode === "trials") {
        let searchTerm = extractedTermForButton;
        try {
          const truncatedQuestion = question.length > 300 ? question.slice(0, 300) : question;
          const extraction = await api.post("/v1/copilot/ask", {
            question: `Extract ONLY the medical condition or disease name being asked about — respond with just the term itself, nothing else, no punctuation, no explanation. Question: "${truncatedQuestion}"`,
            systemPrompt: "You extract a single medical condition/disease term from a question. Respond with only that term.",
            contextId: "accbf-extraction",
          });
          const extracted = (extraction.data?.answer || "").trim().split("\n")[0].replace(/[."']/g, "");
          if (extracted) { searchTerm = extracted; extractedTermForButton = extracted; }
        } catch (extractErr) {
          // Fall back to the local heuristic term — retrieval just won't
          // be quite as precise, not a hard failure. If this call hit the
          // rate limit, the main answer call below will surface that
          // clearly to the user instead.
        }

        // RAG pattern: (1) term extracted above, (2) retrieve real data
        // using that term via the SAME unchanged search endpoint/URL the
        // main page uses, (3) generate a plain-English answer grounded
        // only in that real data.
        const sessionId = localStorage.getItem("accbf_session_id") || "accbf_copilot_lookup";
        let trialData = [];
        try {
          const searchResponse = await api.get("/v1/accbf/search", {
            params: { condition: searchTerm, sources: "clinicaltrials,nci", sessionId },
          });
          trialData = [
            ...(searchResponse.data?.clinicaltrials || []),
            ...(searchResponse.data?.nci || []),
          ];
        } catch (searchErr) {
          trialData = [];
        }

        const context = trialData.length > 0
          ? trialData.map((t, i) => `${i + 1}. ${t.title}${t.org ? ` — ${t.org}` : ""}${t.description ? `\n   ${t.description}` : ""}`).join("\n\n")
          : `No matching trials were found for "${searchTerm}".`;

        payload = {
          question,
          systemPrompt: `${SYSTEM_PROMPT_TRIALS}\n\nREAL TRIAL DATA (from ClinicalTrials.gov and NCI, searched for "${searchTerm}"):\n${context}`,
          contextId: "accbf-trials",
        };
      }

      if (payload.question && payload.question.length > 490) {
        payload.question = payload.question.slice(0, 490);
      }
      const response = await api.post("/v1/copilot/ask", payload);
      const answer = response.data?.answer || "I wasn't able to process that — please try again.";
      setMessages((prev) => [...prev, { role: "assistant", content: answer, searchQuery: extractedTermForButton }]);
    } catch (err) {
      const message = err.response?.status === 429
        ? "You've reached today's free question limit for this assistant. Please try again tomorrow, or continue exploring using the search bar above — that's unaffected."
        : "Connection error — please try again in a moment.";
      setMessages((prev) => [...prev, { role: "assistant", content: message }]);
    } finally {
      setLoading(false);
    }
  };

  // Watches for a per-result "Interpret with Copilot" click from the main
  // page. The result's real data (title/org/description) is already on
  // the page — this just asks Claude to explain that specific data in
  // plain English, without any additional retrieval.
  useEffect(() => {
    if (!interpretRequest) return;
    setOpen(true);
    setMode("general");
    const shortQuestion = "Please explain this in plain, simple, compassionate language for a parent — what it is, and why it might matter for their child.";
    const dataContext = `Title: ${interpretRequest.title}\n${interpretRequest.org ? `Organization: ${interpretRequest.org}\n` : ""}${interpretRequest.description ? `Details: ${interpretRequest.description}` : ""}`;
    sendMessage(shortQuestion, `${SYSTEM_PROMPT}\n\nHere is the specific item to explain:\n${dataContext}`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [interpretRequest]);

  const handleAccept = () => {
    recordAIDisclosureAcceptance();
    setShowDisclosure(false);
    const q = pendingQuestion;
    setPendingQuestion(null);
    if (q) sendMessage(q);
  };
  const handleDecline = () => {
    setShowDisclosure(false);
    setPendingQuestion(null);
  };

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        style={{
          position: "fixed", bottom: 24, right: 24, zIndex: 1000,
          width: 60, height: 60, borderRadius: "50%", border: "none",
          background: ACCENT, color: "white", fontSize: 24, cursor: "pointer",
          boxShadow: "0 8px 24px rgba(180,83,9,0.4)",
        }}
      >
        💬
      </button>
    );
  }

  return (
    <div
      style={{
        position: "fixed",
        bottom: expanded ? 0 : 24,
        right: expanded ? 0 : 24,
        top: expanded ? 0 : "auto",
        left: expanded ? 0 : "auto",
        zIndex: 1000,
        width: expanded ? "100vw" : 380,
        height: expanded ? "100vh" : 560,
        borderRadius: expanded ? 0 : 16,
        background: "#FFFFFF", border: expanded ? "none" : "1px solid #E2E8F0",
        boxShadow: expanded ? "none" : "0 24px 60px rgba(0,0,0,0.15)",
        display: "flex", flexDirection: "column", overflow: "hidden",
        fontFamily: "'DM Sans', sans-serif",
      }}
    >
      {showDisclosure && <AIDisclosureModal onAccept={handleAccept} onDecline={handleDecline} />}

      <div style={{ padding: "16px 18px", background: ACCENT, color: "white", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div style={{ fontWeight: 700, fontSize: 15 }}>ACCBF Assistant</div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <button
            onClick={() => setExpanded((e) => !e)}
            title={expanded ? "Restore" : "Expand"}
            style={{ background: "rgba(255,255,255,0.15)", border: "none", borderRadius: 6, color: "white", fontSize: 13, cursor: "pointer", padding: "4px 8px" }}
          >
            {expanded ? "❚❚" : "⤢"}
          </button>
          <button onClick={() => setOpen(false)} style={{ background: "none", border: "none", color: "white", fontSize: 18, cursor: "pointer" }}>×</button>
        </div>
      </div>

      <div style={{ display: "flex", gap: 6, padding: "10px 16px", borderBottom: "1px solid #E2E8F0" }}>
        <button
          onClick={() => setMode("general")}
          style={{
            flex: 1, padding: "6px 10px", borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: "pointer",
            border: `1px solid ${mode === "general" ? ACCENT : "#E2E8F0"}`,
            background: mode === "general" ? "#FFFBEB" : "white",
            color: mode === "general" ? ACCENT : "#64748B",
          }}
        >
          General Questions
        </button>
        <button
          onClick={() => setMode("trials")}
          style={{
            flex: 1, padding: "6px 10px", borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: "pointer",
            border: `1px solid ${mode === "trials" ? ACCENT : "#E2E8F0"}`,
            background: mode === "trials" ? "#FFFBEB" : "white",
            color: mode === "trials" ? ACCENT : "#64748B",
          }}
        >
          Clinical Trials
        </button>
      </div>

      <div style={{ flex: 1, overflowY: "auto", padding: expanded ? "24px max(24px, calc(50vw - 380px))" : 16, display: "flex", flexDirection: "column", gap: 12 }}>
        {messages.map((m, i) => (
          <div key={i} style={{ alignSelf: m.role === "user" ? "flex-end" : "flex-start", maxWidth: expanded ? "70%" : "85%" }}>
            <div
              style={{
                padding: "10px 14px",
                borderRadius: m.role === "user" ? "14px 4px 14px 14px" : "4px 14px 14px 14px",
                background: m.role === "user" ? ACCENT : "#F1F5F9",
                color: m.role === "user" ? "white" : INK,
                fontSize: expanded ? 14.5 : 13.5,
                lineHeight: 1.7,
              }}
              dangerouslySetInnerHTML={{ __html: renderMarkdown(m.content) }}
            />
            {m.role === "assistant" && m.searchQuery && onSearchRequest && (
              <button
                onClick={() => { onSearchRequest(m.searchQuery); setOpen(false); }}
                style={{
                  marginTop: 6, padding: "6px 12px", borderRadius: 8,
                  border: `1px solid ${ACCENT}`, background: "white", color: ACCENT,
                  fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "inherit",
                }}
              >
                🔍 Search real trials for "{m.searchQuery}"
              </button>
            )}
          </div>
        ))}
        {loading && (
          <div style={{ alignSelf: "flex-start", fontSize: 13, color: "#64748B" }}>Thinking…</div>
        )}
        <div ref={endRef} />
      </div>

      <div style={{ padding: 12, borderTop: "1px solid #E2E8F0", display: "flex", gap: 8, maxWidth: expanded ? 760 : "none", margin: expanded ? "0 auto" : 0, width: expanded ? "100%" : "auto" }}>
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") sendMessage(input); }}
          placeholder="Ask a question…"
          disabled={loading}
          style={{
            flex: 1, padding: "10px 12px", borderRadius: 10,
            border: "1px solid #E2E8F0", fontSize: 13.5, fontFamily: "inherit",
          }}
        />
        <button
          onClick={() => sendMessage(input)}
          disabled={loading || !input.trim()}
          style={{
            padding: "0 16px", borderRadius: 10, border: "none",
            background: (loading || !input.trim()) ? "#E2E8F0" : ACCENT,
            color: "white", cursor: (loading || !input.trim()) ? "default" : "pointer",
            fontWeight: 600, fontSize: 13,
          }}
        >
          →
        </button>
      </div>
    </div>
  );
}